import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import logoWide from '../../assets/logo-wide.png';
import logoFace from '../../assets/logo-face.png';

// Shared nav-link classes so the bottom ACCOUNT link matches the primary nav.
function navLinkClass(active) {
  return [
    'flex items-center py-3 rounded-[10px] border',
    'justify-center px-0 gap-0 side:justify-start side:px-3.5 side:gap-[13px]',
    'font-display transition-colors duration-200 ease-mogged',
    active
      ? 'bg-vein text-bone border-[rgba(255,255,255,0.14)]'
      : 'bg-transparent text-bone-dim border-transparent hover:bg-vein/60 hover:text-bone',
  ].join(' ');
}

// Left-rail navigation for the in-app shell (design-recreation mockup).
// Responsive in two steps:
//   ≥1180px ("side" breakpoint) — 240px rail, wide MOGGED lockup, labelled nav
//   1024–1180px                 — 76px rail, face mark, icon-only nav
//   <1024px                     — hidden; AppLayout switches to BottomTabBar
// The rail never scrolls with the page: the document body scrolls and the
// rail is sticky to the top of the viewport (self-start so it keeps its own
// height rather than stretching to the page).
//
// Props:
//   items: [{ to, label, icon, matchPrefix? }]  primary nav
//   accountItem: { to, label, icon }            pinned to the bottom (replaces
//                                               the old Log Out — sign out lives
//                                               on the account page)
const AppShellSidebar = ({ items, accountItem }) => {
  const location = useLocation();
  const isActive = (item) =>
    location.pathname === item.to
    || (item.matchPrefix && location.pathname.startsWith(item.matchPrefix));
  return (
    <aside
      className={[
        'hidden lg:flex flex-col flex-shrink-0 sticky top-0 self-start h-screen overflow-y-auto',
        'bg-[#161616] border-r border-hairline-soft',
        'w-[76px] px-2.5 side:w-[240px] side:px-[18px]',
        'pt-[30px] pb-[26px]',
      ].join(' ')}
    >
      <div className="flex justify-center items-center pt-0.5 pb-1">
        <img
          src={logoWide}
          alt="MOGGED"
          className="hidden side:block h-10 w-auto max-w-full object-contain mix-blend-screen"
        />
        <img
          src={logoFace}
          alt="MOGGED"
          className="block side:hidden w-[46px] h-[46px] object-contain mix-blend-screen"
        />
      </div>
      <div
        className="h-px mx-auto mt-4 mb-3.5 w-9 side:w-[72px]"
        style={{ background: 'linear-gradient(90deg, rgba(201,164,92,0), #C9A45C, rgba(201,164,92,0))' }}
        aria-hidden="true"
      />
      <div className="h-px bg-hairline-soft mb-4" aria-hidden="true" />

      <nav className="flex flex-col gap-1.5" aria-label="primary navigation">
        {items.map((item) => {
          const active = isActive(item);
          return (
            <Link
              key={item.to}
              to={item.to}
              aria-current={active ? 'page' : undefined}
              className={navLinkClass(active)}
              style={active ? { boxShadow: 'inset 2px 0 0 #C9A45C' } : undefined}
            >
              <span className="inline-flex items-center justify-center w-4 h-4" aria-hidden="true">
                {item.icon}
              </span>
              <span className="hidden side:inline text-xs tracking-[0.2em] uppercase">
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto px-1.5">
        {accountItem && (
          <>
            <div className="h-px bg-hairline-soft mt-[18px] mb-1.5" aria-hidden="true" />
            <Link
              to={accountItem.to}
              aria-current={isActive(accountItem) ? 'page' : undefined}
              className={navLinkClass(isActive(accountItem))}
              style={isActive(accountItem) ? { boxShadow: 'inset 2px 0 0 #C9A45C' } : undefined}
            >
              <span className="inline-flex items-center justify-center w-4 h-4" aria-hidden="true">
                {accountItem.icon}
              </span>
              <span className="hidden side:inline text-xs tracking-[0.2em] uppercase">
                {accountItem.label}
              </span>
            </Link>
          </>
        )}
      </div>
    </aside>
  );
};

export default AppShellSidebar;
